import { type ComponentProps, type ReactNode, useRef, useState } from 'react';
import {
  AlertDialog,
  AlertDialogButton,
  Text,
  TextField,
  theme,
} from '@frameio/vapor';
import { IconCheckmark } from '@frameio/vapor-icons';
import styles from './DeleteConfirmation.module.css';
import TEXT from './generated/TEXT/DeleteConfirmation';

const DELETE_CONFIRMATION = 'delete';
const CHECKMARK_SIZE = 16;

TEXT.DEFINE({
  /** Button to cancel the delete confirmation and close the dialog */
  cancel: 'Cancel',
  /** Instruction prompting the user to type the confirmation text to proceed with deletion */
  confirmationInstructions: (confirmationText: string) =>
    `Type "${confirmationText}" to confirm`,
});

type AlertDialogProps = ComponentProps<typeof AlertDialog>;

type DeleteConfirmationProps = Pick<
  AlertDialogProps,
  'isShowing' | 'stackActionButtons'
> &
  Required<Pick<AlertDialogProps, 'onConfirm' | 'onRequestClose'>> & {
    /** Text used in the confirm button. */
    confirmButtonText: string;
    /** Override the default text the user must type to confirm. Defaults to "delete". */
    confirmInputText?: string;
    /** Body content rendered in the dialog. */
    message: ReactNode;
    /** When true, the user must type the confirmation text before confirming. Defaults to true. */
    requireExtraConfirmation?: boolean;
    /** The dialog title. */
    title: string;
  };

/**
 * The DeleteConfirmation component can be used to confirm whether a user really
 * wants to delete a resource. It requires that the user type the word "delete"
 * into an input before being able to click "Confirm," but that can be disabled
 * via the requireExtraConfirmation prop.
 */
export function DeleteConfirmation({
  confirmButtonText,
  confirmInputText = DELETE_CONFIRMATION,
  isShowing,
  message,
  onConfirm,
  onRequestClose,
  requireExtraConfirmation = true,
  title,
  stackActionButtons,
  ...otherProps
}: DeleteConfirmationProps) {
  const [confirmation, setConfirmation] = useState('');
  const initialFocusRef = useRef<HTMLInputElement>(null);

  const isButtonDisabled =
    confirmation.toLowerCase() !== confirmInputText.toLowerCase() &&
    requireExtraConfirmation;

  const handleSubmit = (event: React.FormEvent) => {
    event.preventDefault();

    if (!isButtonDisabled) {
      onConfirm();
      onRequestClose();
    }
  };

  const handleCancelKeyDown = (event: React.KeyboardEvent) => {
    if (event.key === 'Enter') {
      event.preventDefault();
      onRequestClose();
    }
  };

  const handleSubmitKeyDown = (event: React.KeyboardEvent) => {
    if (event.key === 'Enter' && !isButtonDisabled) {
      event.preventDefault();
      onConfirm();
      onRequestClose();
    }
  };

  const handleDialogEntered = () => {
    initialFocusRef.current?.focus();
  };

  const handleDialogExited = () => {
    setConfirmation('');
  };

  return (
    <AlertDialog
      actions={
        <>
          <AlertDialogButton
            color="secondary"
            onClick={onRequestClose}
            onKeyDown={handleCancelKeyDown}
          >
            {TEXT.cancel}
          </AlertDialogButton>
          <AlertDialogButton
            ref={requireExtraConfirmation ? undefined : initialFocusRef}
            color="error"
            data-testid="delete-confirmation-dialog--delete-button"
            disabled={isButtonDisabled}
            onClick={handleSubmit}
            onKeyDown={handleSubmitKeyDown}
            type="submit"
          >
            {confirmButtonText}
          </AlertDialogButton>
        </>
      }
      aria-labelledby="title"
      details={
        requireExtraConfirmation ? (
          <form onSubmit={handleSubmit}>
            <Text variant="body">{message}</Text>
            <div className={styles.inputContainer}>
              <TextField
                ref={initialFocusRef}
                adornmentAfter={
                  <>
                    {!isButtonDisabled && (
                      <IconCheckmark
                        color={theme.colors.icon.interactive.success}
                        size={CHECKMARK_SIZE}
                      />
                    )}
                  </>
                }
                data-testid="delete-confirmation-form-input"
                helpText={String(
                  TEXT.confirmationInstructions(confirmInputText)
                )}
                hideLabel={true}
                label={String(TEXT.confirmationInstructions(confirmInputText))}
                maxLength={confirmInputText.length}
                onChange={(event) => setConfirmation(event.target.value)}
                value={confirmation}
              />
            </div>
          </form>
        ) : (
          <Text variant="body">{message}</Text>
        )
      }
      hasOutermostTheme={true}
      isShowing={isShowing}
      onConfirm={onConfirm}
      onEntered={handleDialogEntered}
      onExited={handleDialogExited}
      onRequestClose={onRequestClose}
      stackActionButtons={stackActionButtons}
      title={title}
      variant="error"
      {...otherProps}
    />
  );
}
